import { BackgroundColorsMap, GradientsColorsMap, MilanBackgroundColors, MilanGradientColors } from "@milanlaser/mlcomponents";
import { useMemo } from "react";

export type ImageObject = { [key: string]: any };
export type ImagesInput = ImageObject | ImageObject[];


type S19TimelineDataType = {
  id: number;
  date: string; // ISO date string or empty
  icon: string; // URL to the icon image
  image: string; // URL to the main image
  imageAlt: string; // Alt text for the image
  title: string; // Event title
};

export type S19TimelineDataTypes = S19TimelineDataType[];


/**
 * Updates an object by checking if the specified key has a value of 'None'
 * and sets it to undefined if true, otherwise keeps the original value
 * 
 **/

export const processBackgroundColor = <T extends Record<string, any>>(arg1: T) => {
  // Get the only key in the object
  const key = Object.keys(arg1)[0];

  // If the value is 'None', return undefined
  if (arg1[key] === 'None') {
    return 'default'; //default is equivalent to transparent in MLComponents
  }

  // Otherwise return the original value
  return arg1[key];
};


/**
 * Maps background color and gradient values to their respective CSS classes
 *
 * @param {string} backgroundColor - The background color value
 * @param {string} gradientColor - The gradient value
 * @returns {string} - The CSS class for the background color and gradient
 */
export const processBackgroundAndGradientColorMap = ({ backgroundColor, gradientColor }: { backgroundColor: MilanBackgroundColors | 'None', gradientColor: MilanGradientColors | 'None' }) => {
  // Map of background colors to CSS classes

  const bgColor = backgroundColor === 'None' ? 'default' : backgroundColor;
  const bgGradientColor = gradientColor === 'None' ? 'default' : gradientColor;

  const newBackgroundColor = BackgroundColorsMap[bgColor || 'default'];
  // Map of gradients to CSS classes
  const newGradientColor = GradientsColorsMap[bgGradientColor || 'default'];

  // Return the combined CSS classes
  return `${newBackgroundColor} ${newGradientColor}`;
};


/**
 * Hook that generates a unique key from an array of image objects
 * to trigger re-renders when image sources change
 */
export const useImageKey = (imagesInput: ImagesInput): string => {
  // Common keys used for image sources, in priority order
  const possibleImageKeys = ['src', 'imageLink', 'image', 'before', 'after', 'link', 'url', 'iconName', 'iconSrc', 'icon'];

  // Ensure we're working with an array
  const images = Array.isArray(imagesInput)
    ? imagesInput
    : [imagesInput];

  // Find image source value from an object using possible keys
  const extractImageSource = (item: ImageObject, index: number): string => {
    // Special handling for objects that have both before and after properties
    if ('before' in item && 'after' in item) {
      return `before_${item.before}_after_${item.after}_${index}`;
    }

    // Find the first matching key that exists in the item
    const foundKey = possibleImageKeys?.find(key =>
      key in item && item[key] !== null && item[key] !== undefined
    );

    let sourceValue = '';

    if (foundKey) {
      const value = item[foundKey];
      // Handle different value types
      if (typeof value === 'string') sourceValue = value;
      else if (typeof value === 'object') sourceValue = JSON.stringify(value);
      else sourceValue = String(value);
    } else {
      // If no matching key found, use the object's JSON representation
      sourceValue = JSON.stringify(item);
    }

    // Append the index to make duplicate named images unique
    return `${sourceValue}_${index}`;
  };

  // Map each image object to its source value with index
  const imageSources = images.map(extractImageSource);

  // Join all sources to create a unique key
  return imageSources.join('-');
};



export function useArrayKey(arr: any[]): string {
  return useMemo(() => arr.map((item, i) => `${JSON.stringify(item)}_${i}`).join('-'), [arr]);
}

export const transformToS19TimelineData = (timelineProps: S19TimelineDataTypes) => ({
  dates: timelineProps.map((props) => props.date),
  titles: timelineProps.map((props) => props.title),
  icons: timelineProps.map((props) => props.icon),
  images: timelineProps.map((props) => props.image),
  imageAltTexts: timelineProps.map((props) => props.imageAlt),
});

// Helper function to get video sources with different formats
export const getVideoSources = (videoUrl: string) => {
  const baseUrl = videoUrl.replace(/\.(mp4|webm|ogg)$/i, '');
  return [
    { src: `${baseUrl}.webm`, type: 'video/webm' },
    { src: `${baseUrl}.ogg`, type: 'video/ogg' },
    { src: videoUrl, type: 'video/mp4' }
  ];
};
